import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { APIURL, TZ } from '../../../Global';
import { useAlert } from 'react-alert';
import { optionsList } from '../AddMedicines/formOptions';
import { sampleList } from './sampleList';
import TestParameterRow from './TestParameterRow';
import CustomSelect from '../AddMedicines/CustomSelect';
import { IoCopyOutline } from "react-icons/io5";

const TestDetails = ({ type, selected, selectedTest, selectedTestGroup, handleSuccess, sample, data }) => {
    const [testData, setTestData] = useState({
        id: '',
        sample: '',
        name: '',
        unit: '',
        note: '',
        input_type: '',
    });

    const [refRange, setRefRange] = useState([
        {
            category: '',
            min_value: '',
            max_value: '',
        },
    ]);

    const [panels, setPanels] = useState([]);
    const [tests, setTests] = useState([]);
    const [error, setError] = useState({
        sample: '',
        panels: '',
        tests: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const alert = useAlert();
    const samples = React.useMemo(() =>
        sampleList?.map((item) => ({ value: item, label: item })) || [],
        []
    );

    const panelsList = React.useMemo(() =>
        data?.panels?.map((panel) => ({
            value: panel.id,
            label: panel.panel_name,
        })) || [],
        [data?.panels]
    );

    const testsList = React.useMemo(() =>
        data?.tests?.map((test) => ({
            value: test.id,
            label: test.test_name,
        })) || [],
        [data?.tests]
    );

    useEffect(() => {
        if (selected === 'testgroup' && selectedTestGroup) {
            setTestData({
                id: selectedTestGroup.value || '',
                sample: '',
                name: selectedTestGroup.label || '',
                unit: '',
                note: selectedTest?.note || '',
            });

            if (selectedTest?.panels) {
                const selectedPanelIds = selectedTest.panels.map(panel => panel.id);
                const selectedPanels = data.panels
                    .filter(panel => selectedPanelIds.includes(panel.id))
                    .map(panel => ({
                        value: panel.id,
                        label: panel.panel_name,
                    }));
                setPanels(selectedPanels);
            }

            if (selectedTest?.tests) {
                const selectedTestIds = selectedTest.tests.map(test => test.id);
                const selectedTests = data.tests
                    .filter(test => selectedTestIds.includes(test.id))
                    .map(test => ({
                        value: test.id,
                        label: test.test_name,
                        selectedOption: { value: test.test_name, label: test.test_name },
                        unit: test.unit || '',
                        order: test.order || '',
                        value: test.value || '',
                        note: test.note || '',
                        reference_parameter: (test.reference_parameter && test.reference_parameter.length > 0)
                            ? test.reference_parameter
                            : [{ category: '', min_value: '', max_value: '' }]
                    }));
                setTests(selectedTests);
            } else {
                setTests([]);
            }

            setRefRange([{ category: '', min_value: '', max_value: '' }]);

        } else if (selectedTest) {
            setTestData({
                id: selectedTest.id || '',
                sample: selectedTest.sample || selectedTest.sample_type || sample || '',
                name: selectedTest.test_name || selectedTest.panel_name || '',
                unit: selectedTest.unit || '',
                note: selectedTest.note || '',
                input_type: selectedTest.input_type
            });

            if (selectedTest.reference_parameter && Array.isArray(selectedTest.reference_parameter)) {
                setRefRange(selectedTest.reference_parameter);
            } else {
                setRefRange([{ category: '', min_value: '', max_value: '' }]);
            }

            if (selected === 'panels' && selectedTest.id && data?.tests) {
                const filteredTests = data.tests.filter((test) =>
                    test?.related_panel_ids?.includes(selectedTest.id)
                );

                setTests(
                    filteredTests.map((test) => ({
                        value: test.id,
                        label: test.test_name,
                        selectedOption: { value: test.test_name, label: test.test_name },
                        unit: test.unit || '',
                        order: test.order || '',
                        value: test.value || '',
                        note: test.note || '',
                        reference_parameter: (test.reference_parameter && test.reference_parameter.length > 0)
                            ? test.reference_parameter
                            : [{ category: '', min_value: '', max_value: '' }]
                    }))
                );
                setPanels([]);

            } else if (selected === 'test' && selectedTest.related_panel_ids && data?.panels) {
                const relatedPanels = selectedTest.related_panel_ids
                    .map((panelId) => {
                        const panel = data.panels.find((panel) => panel.id === panelId);
                        return panel ? { value: panelId, label: panel.panel_name } : null;
                    })
                    .filter(Boolean);
                setPanels(relatedPanels);
                if (selectedTest.test_parameters && Array.isArray(selectedTest.test_parameters)) {
                    const formattedTests = selectedTest.test_parameters.map(param => ({
                        id: param.id,
                        selectedOption: { value: param.test_parameter, label: param.test_parameter },
                        unit: param.unit || '',
                        order: param.order?.toString() || '',
                        value: param.input_type,
                        note: param.note || '',
                        reference_parameter: (param.reference_parameter && param.reference_parameter.length > 0)
                            ? param.reference_parameter
                            : [{ category: '', min_value: '', max_value: '' }],
                        sub_tests: (param.sub_parameters || []).map(sub => ({
                            
                            selectedOption: {
                                value: sub.name || sub.test_parameter || "",
                                label: sub.name || sub.test_parameter || ""
                            },
                            unit: sub.unit || '',
                            order: sub.order?.toString() || '',
                            value: sub.value || sub.input_type,
                            note: sub.note || '',
                            calculation_type: sub.calculation_type || '',

                            reference_parameter: (sub.ref_range || sub.reference_parameter || []).length > 0
                                ? (sub.ref_range || sub.reference_parameter)
                                : [{ category: '', min_value: '', max_value: '' }]
                        }))
                    }));
                    setTests(formattedTests);
                } else {
                    setTests([]);
                }
            }
        }

        setError({ sample: '', panels: '', tests: '' });
    }, [selectedTest, selectedTestGroup, data?.panels, data?.tests, sample, selected]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTestData((current) => ({
            ...current,
            [name]: value,
        }));

        // Clear related errors
        if (name === 'sample') {
            setError((current) => ({ ...current, sample: '' }));
        }
    };

    const handleSampleChange = (selectedOption) => {
        const value = selectedOption ? selectedOption.value : '';
        setTestData((current) => ({
            ...current,
            sample: value,
        }));
        setError((current) => ({ ...current, sample: '' }));
    };

    const handlePanelChange = (selectedOptions) => {
        setPanels(selectedOptions || []);
        setError((current) => ({ ...current, panels: '' }));
    };

    const handleTestChange = (selectedOptions) => {
        setTests(selectedOptions || []);
        setError((current) => ({ ...current, tests: '' }));
    };

    const validateForm = () => {
        const newErrors = { sample: '', panels: '', tests: '' };
        let isValid = true;

        // Validate reference ranges for tests
        if (selected === 'test') {
            const invalidRanges = refRange.some(range => {
                const hasSomeFields = range.category || range.min_value || range.max_value;
                const hasAllFields = range.category && range.min_value && range.max_value;
                return hasSomeFields && !hasAllFields;
            });

            if (invalidRanges) {
                alert.error('Please complete all fields for each reference range (category, min, and max values)');
                isValid = false;
            }
        }

        setError(newErrors);
        return isValid;
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            return;
        }
        setIsLoading(true);
        let basePayload = {};

        if (selected === 'testgroup') {
            basePayload = {
                group_id: testData.id,
                test_ids: tests?.map((test) => test.value?.toString()) ?? [],
                panel_ids: panels?.map((panel) => panel.value?.toString()) ?? [],
                note: testData.note || ''
            }
        } else {
           basePayload = {
            [selected === 'test' ? 'test_id' : 'panel_id']: selectedTest.id, 
            [selected === 'test' ? 'test_name' : 'panel_name']: testData.name,
            note: testData.note || '',
        };

            if (selected === 'test') {
                basePayload.panel_ids = panels.map((panel) => panel.value);
                basePayload.unit = testData.unit;
                basePayload.sample = testData.sample;
                basePayload.reference_parameter = refRange.filter(range =>
                    range.category && range.min_value && range.max_value
                );
                basePayload.parameters = tests.map((test, index) => ({
                    id: test.id,
                    test_parameter: test.selectedOption?.value || '',
                    input_type: test.value,
                    is_required: true,
                    unit: test.unit || '',
                    order: parseInt(test.order) || index + 1,
                    note: test.note || '',
                    reference_parameter: (test.reference_parameter || []).filter(range =>
                        range.category && range.min_value && range.max_value
                    ),
                    sub_parameters: (test.sub_tests || []).map((sub, subIndex) => ({
                        name: sub.selectedOption?.value || '',
                        input_type: sub.value,
                        unit: sub.unit || '',
                        order: parseInt(sub.order) || subIndex + 1,
                        calculation_type: sub.calculation_type || '',
                        note: sub.note || '',
                        reference_parameter: (sub.reference_parameter || []).filter(range =>
                            range.category && range.min_value && range.max_value
                        ),
                    }))
                }));
            } else if (selected === 'panels') {
                basePayload.test_ids = tests.map((test) => test.value);
            }

            if (type === 'testgroup' && testData.sample) {
                basePayload.sample_type = testData.sample;
            }
        }

        // Get token
        const tokenString = sessionStorage.getItem('usertoken');
        if (!tokenString) {
            alert.error('Authentication token not found. Please login again.');
            return;
        }

        const token = tokenString.replace(/["]+/g, '');

        // Determine API endpoint based on selection type
        const apiEndpoint = selected === 'testgroup'
            ? `${APIURL}/api/v1/staff/master-test-group/?${TZ}`
            : `${APIURL}/api/v1/staff/lab-test/?${TZ}`;

        axios
            .put(apiEndpoint, basePayload, {
                headers: {
                    Authorization: `Token ${token}`,
                    'Content-Type': 'application/json',
                },
            })
            .then((res) => {
                setIsLoading(false);
                if (res.status === 200 && res.data?.status === 'success') {
                    alert.success('Updated successfully!');
                    if (res.data.existed_test && res.data.existed_test.length > 0) {
                        alert.info(
                            `The following tests already exist and were not added: ${res.data.existed_test.join(', ')}`,
                            { timeout: 10000 }
                        );
                    }

                    if (res.data.existed_panel && res.data.existed_panel.length > 0) {
                        alert.info(
                            `The following tests already exist and were not added: ${res.data.existed_panel.join(', ')}`,
                            { timeout: 10000 }
                        );
                    }

                    if (handleSuccess && typeof handleSuccess === 'function') {
                        handleSuccess();
                    }
                } else {
                    alert.error(res.data?.message || `Failed to update ${selected}.`);
                }
            })
            .catch((err) => {
                console.error('Update error:', err);
                const errorMessage = err.response?.data?.message ||
                    err.response?.data?.error ||
                    `Failed to update ${selected}. Please try again.`;
                alert.error(errorMessage);
            });
    };

    if (!selectedTest && !selectedTestGroup) {
        return <div>No item selected</div>;
    }

    const getInputLabel = () => {
        if (selected === 'testgroup') return 'Test Group Name';
        if (selected === 'test') return 'Test Name';
        return 'Panel Name';
    };
    const addTestRow = () => {
        let nextOrder = 0;
        const existingOrders = tests.map(t => parseInt(t.order)).filter(num => !isNaN(num));
        if (existingOrders.length > 0) nextOrder = Math.max(...existingOrders) + 1;
        if (nextOrder > 10) nextOrder = '';

        setTests((prev) => [
            ...prev,
            {
                selectedOption: null,
                unit: '',
                note: '',
                order: nextOrder.toString(),
                value: '',
                reference_parameter: [{ category: '', min_value: '', max_value: '' }],
                sub_tests: []
            }
        ]);
    };

    const handleAddSubTest = (parentIndex) => {
        const updatedTests = [...tests];


        if (!updatedTests[parentIndex].sub_tests) {
            updatedTests[parentIndex].sub_tests = [];
        }


        updatedTests[parentIndex].sub_tests.push({
            selectedOption: null,
            unit: '',
            note: '',
            order: '',
            value: '',
            reference_parameter: [{ category: '', min_value: '', max_value: '' }]
        });
        setTests(updatedTests);
    };


    const removeTestRow = (index, subIndex = null) => {
        const updatedTests = [...tests];
        if (subIndex !== null) {

            updatedTests[index].sub_tests = updatedTests[index].sub_tests.filter((_, i) => i !== subIndex);
        } else {

            updatedTests.splice(index, 1);
        }
        setTests(updatedTests);
    };

    const handleTestFieldChange = (index, field, value, subIndex = null) => {
        const updatedTests = [...tests];
        if (subIndex !== null) {
            updatedTests[index].sub_tests[subIndex] = { ...updatedTests[index].sub_tests[subIndex], [field]: value };
        } else {
            updatedTests[index] = { ...updatedTests[index], [field]: value };
        }
        setTests(updatedTests);
        if (field === 'selectedOption') setError((prev) => ({ ...prev, tests: '' }));
    };


    const handleNestedRefChange = (testIndex, refIndex, field, value, subIndex = null) => {
        const updatedTests = [...tests];
        if (subIndex !== null) {
            const ranges = [...updatedTests[testIndex].sub_tests[subIndex].reference_parameter];
            ranges[refIndex] = { ...ranges[refIndex], [field]: value };
            updatedTests[testIndex].sub_tests[subIndex].reference_parameter = ranges;
        } else {
            const ranges = [...updatedTests[testIndex].reference_parameter];
            ranges[refIndex] = { ...ranges[refIndex], [field]: value };
            updatedTests[testIndex].reference_parameter = ranges;
        }
        setTests(updatedTests);
    };

    const addNestedRefRange = (testIndex, subIndex = null) => {
        const updatedTests = [...tests];
        const newRef = { category: '', min_value: '', max_value: '' };
        if (subIndex !== null) {
            updatedTests[testIndex].sub_tests[subIndex].reference_parameter.push(newRef);
        } else {
            updatedTests[testIndex].reference_parameter.push(newRef);
        }
        setTests(updatedTests);
    };

    const removeNestedRefRange = (testIndex, refIndex, subIndex = null) => {
        const updatedTests = [...tests];
        if (subIndex !== null) {
            const ranges = updatedTests[testIndex].sub_tests[subIndex].reference_parameter;
            if (ranges.length > 1) {
                updatedTests[testIndex].sub_tests[subIndex].reference_parameter = ranges.filter((_, i) => i !== refIndex);
            }
        } else {
            const ranges = updatedTests[testIndex].reference_parameter;
            if (ranges.length > 1) {
                updatedTests[testIndex].reference_parameter = ranges.filter((_, i) => i !== refIndex);
            }
        }
        setTests(updatedTests);
    };

    const handleMainRefChange = (index, field, value) => {
        const updatedRefs = [...refRange];
        updatedRefs[index] = { ...updatedRefs[index], [field]: value };
        setRefRange(updatedRefs);
    };

    const addMainRefRange = () => {
        setRefRange((prev) => [...prev, { category: '', min_value: '', max_value: '' }]);
    };

    const removeMainRefRange = (index) => {
        const updatedRefs = refRange.filter((_, i) => i !== index);
        setRefRange(updatedRefs);
    };
    const specificTestOptions = [
        { value: 'Eosinophils', label: 'Eosinophils' },
        { value: 'Lymphocytes', label: 'Lymphocytes' },
        { value: 'Neutrophils', label: 'Neutrophils' },
        { value: 'Monocytes', label: 'Monocytes' },
        { value: 'MPV', label: 'MPV' },
    ];
    const valueOptions = [
        { value: 'boolean', label: 'Boolean' },
        { value: 'text', label: 'Character' },
        { value: 'numeric', label: 'Numeric' },
    ];


    return (
        <div style={{ height: "454px", overflowY: "auto", padding: "10px" }}>
            <div className="panel-container">
                <div className='pricing-inputcontainer'>
                    <span>{getInputLabel()}</span>
                    <input
                        name="name"
                        placeholder='Name'
                        value={testData.name}
                        onChange={handleInputChange}
                    />
                </div>

                {type === 'testgroup' && selected !== 'testgroup' && (
                    <div className='pricing-inputcontainer'>
                        <span>Sample</span>
                        <Select
                            className='input-container'
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    minHeight: '40px',
                                    border: error.sample ? '1px solid red' : '1px solid #E3E3E3',
                                }),
                                menu: (provided) => ({
                                    ...provided,
                                    zIndex: 100,
                                }),
                                menuList: (provided) => ({
                                    ...provided,
                                    maxHeight: '200px',
                                    zIndex: 100,
                                }),
                            }}
                            value={samples.find(option => option.value === testData.sample) || null}
                            onChange={handleSampleChange}
                            options={samples}
                            placeholder="Select sample"
                            isClearable
                        />
                        {error.sample && <p style={{ color: 'red', margin: '5px 0 0 0' }}>{error.sample}</p>}

                    </div>

                )}
                <div className='pricing-inputcontainer'>
                    <span>Value</span>
                    <CustomSelect
                        options={valueOptions}
                        value={valueOptions.find(opt => opt.value === testData.input_type) || null}
                        onChange={(selectedOption) => setTestData(prev => ({
                            ...prev,
                            input_type: selectedOption ? selectedOption.value : ''
                        }))}
                        placeholder="Select Value"
                    />
                </div>
                <div className='pricing-inputcontainer'>
                    <span>Note</span>
                    <input
                        name="note"
                        placeholder='Note'
                        value={testData.note}
                        onChange={handleInputChange}
                    />
                </div>
                {(selected === 'test') && (
                    <>
                        <div className='pricing-inputcontainer'>
                            <span>Reference Range</span>
                            {refRange.map((range, refIndex) => (
                                <div key={refIndex} style={{ display: 'flex', gap: '5px', marginBottom: '10px', alignItems: 'center' }}>
                                    <div style={{ flex: 1, padding: 0 }}>
                                        <CustomSelect
                                            options={optionsList}
                                            placeholder='Category'
                                            value={optionsList?.find(opt => opt.value === range.category) || null}
                                            onChange={(opt) => handleMainRefChange(refIndex, 'category', opt ? opt.value : '')}
                                        />
                                    </div>
                                    <input
                                        className='col-3'
                                        placeholder='Min'
                                        value={range.min_value}
                                        onChange={(e) => handleMainRefChange(refIndex, 'min_value', e.target.value)}
                                    />
                                    <input
                                        className='col-3'
                                        placeholder='Max'
                                        value={range.max_value}
                                        onChange={(e) => handleMainRefChange(refIndex, 'max_value', e.target.value)}
                                    />
                                    {refIndex > 0 && (
                                        <button
                                            type="button"
                                            title="Copy from above"
                                            style={{ cursor: 'pointer', background: 'none', border: 'none', borderRadius: '4px' }}
                                            onClick={() => {
                                                const prevRange = refRange[refIndex - 1];
                                                const updatedRefs = [...refRange];
                                                updatedRefs[refIndex] = {
                                                    ...updatedRefs[refIndex],
                                                    // category: prevRange.category,
                                                    min_value: prevRange.min_value,
                                                    max_value: prevRange.max_value
                                                };
                                                setRefRange(updatedRefs);
                                            }}
                                        >
                                            <IoCopyOutline />
                                        </button>
                                    )}
                                    {refRange.length > 1 && (
                                        <button
                                            type="button"
                                            className='remove-btn'
                                            onClick={() => removeMainRefRange(refIndex)}
                                            style={{ background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '25px', height: '25px', cursor: 'pointer' }}
                                        > - </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                className='add-btn'
                                style={{ width: '100%', marginTop: '5px' }}
                                onClick={addMainRefRange}
                            > + Add Range </button>
                        </div>
                        {type !== 'global' && (
                            <div className='pricing-inputcontainer'>
                                <span>Parameter</span>
                                {tests.map((testItem, testIndex) => (
                                    <div
                                        key={testIndex}
                                        style={{
                                            position: 'relative',
                                            border: '1px solid #E3E3E3',
                                            padding: '25px 15px 15px 15px',
                                            borderRadius: '8px',
                                            marginBottom: '15px',
                                            backgroundColor: '#fafafa'
                                        }}
                                    >
                                        <TestParameterRow
                                            item={testItem}
                                            parentIndex={testIndex}
                                            tests={tests}
                                            handleTestFieldChange={handleTestFieldChange}
                                            handleNestedRefChange={handleNestedRefChange}
                                            addNestedRefRange={addNestedRefRange}
                                            removeNestedRefRange={removeNestedRefRange}
                                            removeTestRow={removeTestRow}
                                            handleAddSubTest={handleAddSubTest}
                                            specificTestOptions={specificTestOptions}
                                            valueOptions={valueOptions}
                                            optionsList={optionsList}
                                            error={error.tests}
                                        />

                                        {testItem.sub_tests && testItem.sub_tests.length > 0 && (
                                            <div style={{ marginLeft: '30px', marginTop: '10px', borderLeft: '3px solid #ccc', paddingLeft: '15px' }}>
                                                <h6 style={{ fontSize: '14px', color: '#666', fontWeight: '600' }}>Subparameters</h6>
                                                {testItem.sub_tests.map((subItem, subIndex) => (
                                                    <TestParameterRow
                                                        key={subIndex}
                                                        item={subItem}
                                                        parentIndex={testIndex}
                                                        subIndex={subIndex}
                                                        tests={tests}
                                                        handleTestFieldChange={handleTestFieldChange}
                                                        handleNestedRefChange={handleNestedRefChange}
                                                        addNestedRefRange={addNestedRefRange}
                                                        removeNestedRefRange={removeNestedRefRange}
                                                        removeTestRow={removeTestRow}
                                                        specificTestOptions={specificTestOptions}
                                                        valueOptions={valueOptions}
                                                        optionsList={optionsList}
                                                        error={error.tests}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className='add-btn'
                                            onClick={() => handleAddSubTest(testIndex)}
                                        >
                                            {testItem.sub_tests && testItem.sub_tests.length > 0
                                                ? "+ Add Another Subparameter Test"
                                                : "4. Add New Subparameter Test"
                                            }
                                        </button>
                                    </div>
                                ))}

                                {error.tests && <p style={{ color: 'red', margin: '5px 0 0 0' }}>{error.tests}</p>}

                                <button
                                    type="button"
                                    className='add-btn'
                                    onClick={addTestRow}
                                    style={{ marginTop: '10px' }}
                                >
                                    {tests.length > 0 ? "+ Add Another Parameter" : "Add New Parameter"}
                                </button>
                            </div>
                        )}
                    </>
                )}

                {(selected === 'panels' || selected === 'testgroup') && (
                    <div className='pricing-inputcontainer'>
                        <span>Tests</span>
                        <Select
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    width: '100%',
                                    minHeight: '44px',
                                    border: error.tests ? '1px solid red' : '1px solid #E3E3E3',
                                }),
                                menu: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                }),
                                menuList: (provided) => ({
                                    ...provided,
                                    maxHeight: '200px',
                                    zIndex: 9999,
                                }),
                            }}
                            isMulti
                            value={tests}
                            onChange={handleTestChange}
                            options={testsList}
                            placeholder="Select Tests"
                            isClearable
                        />
                        {error.tests && <p style={{ color: 'red', margin: '5px 0 0 0' }}>{error.tests}</p>}
                    </div>
                )}

                {selected === 'testgroup' && (
                    <div className='pricing-inputcontainer'>
                        <span>Panels</span>
                        <Select
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    width: '100%',
                                    minHeight: '44px',
                                    border: error.panels ? '1px solid red' : '1px solid #E3E3E3',
                                }),
                                menu: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                }),
                                menuList: (provided) => ({
                                    ...provided,
                                    maxHeight: '200px',
                                    zIndex: 9999,
                                }),
                            }}
                            isMulti
                            value={panels}
                            onChange={handlePanelChange}
                            options={panelsList}
                            placeholder="Select Panels"
                            isClearable
                        />
                        {error.panels && <p style={{ color: 'red', margin: '5px 0 0 0' }}>{error.panels}</p>}
                    </div>
                )}
                {/* button */}
                <button
                    type="button"
                    className='add-btn'
                    style={{ margin: "auto auto 10px auto" }}
                    onClick={handleSubmit}
                    ondisabled={isLoading}
                >
                    {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </div>
    );
};

export default TestDetails;